import React, { useState, useEffect, useCallback } from 'react';
import {
  Show,
  TextField,
  EmailField,
  DateField,
  BooleanField,
  NumberField,
  UrlField,
  FunctionField,
  TabbedShowLayout,
  Tab,
  useTranslate,
} from 'react-admin';
import { Card, CardContent, CardHeader, Avatar, Typography, List, ListItem, ListItemAvatar, ListItemText, Divider } from '@mui/material';

// Компонент для отображения подписчиков
const FollowersLoader = ({ userId }: { userId: string }) => {
  const [followers, setFollowers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const translate = useTranslate();

  const loadFollowers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('http://localhost:4000/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({
          query: `
            query GetFollowers($userId: String!) {
              getFollowers(userId: $userId) {
                id
                name
                email
                avatarUrl
                profession
                location
              }
            }
          `,
          variables: { userId }
        })
      });
      const result = await response.json();
      if (result.errors) throw new Error(result.errors[0].message);
      setFollowers(result.data.getFollowers || []);
    } catch (err: any) {
      console.error('Error loading followers:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) loadFollowers();
  }, [userId, loadFollowers]);

  if (loading) return <div style={{ padding: '16px' }}>{translate('resources.users.social.loading_followers')}</div>;
  if (error) return <div style={{ padding: '16px', color: 'red' }}>{translate('resources.users.social.error')}: {error}</div>;
  if (followers.length === 0) return <div style={{ padding: '16px', color: '#666' }}>{translate('resources.users.social.no_followers')}</div>;

  return (
    <Card style={{ margin: '16px 0' }}>
      <CardHeader title={`👥 Подписчики (${followers.length})`} />
      <CardContent>
        <List>
          {followers.map((follower: any, index: number) => (
            <React.Fragment key={follower.id}>
              <ListItem>
                <ListItemAvatar>
                  <Avatar src={follower.avatarUrl} alt={follower.name}>
                    {follower.name?.charAt(0)?.toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography variant="subtitle1" fontWeight="bold">
                      {follower.name}
                    </Typography>
                  }
                  secondary={
                    <div>
                      <Typography variant="body2" color="textSecondary">
                        {follower.email}
                      </Typography>
                      {follower.profession && (
                        <Typography variant="caption" color="textSecondary">
                          {follower.profession}
                          {follower.location && ` • ${follower.location}`}
                        </Typography>
                      )}
                    </div>
                  }
                />
              </ListItem>
              {index < followers.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

// Компонент для отображения подписок
const FollowingLoader = ({ userId }: { userId: string }) => {
  const [following, setFollowing] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadFollowing = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('http://localhost:4000/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({
          query: `
            query GetFollowing($userId: String!) {
              getFollowing(userId: $userId) {
                id
                name
                email
                avatarUrl
                profession
                location
              }
            }
          `,
          variables: { userId }
        })
      });
      const result = await response.json();
      if (result.errors) throw new Error(result.errors[0].message);
      setFollowing(result.data.getFollowing || []);
    } catch (err: any) {
      console.error('Error loading following:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) loadFollowing();
  }, [userId, loadFollowing]);

  if (loading) return <div style={{ padding: '16px' }}>Загрузка подписок...</div>;
  if (error) return <div style={{ padding: '16px', color: 'red' }}>Ошибка: {error}</div>;
  if (following.length === 0) return <div style={{ padding: '16px', color: '#666' }}>Нет подписок</div>;

  return (
    <Card style={{ margin: '16px 0' }}>
      <CardHeader title={`👤 Подписки (${following.length})`} />
      <CardContent>
        <List>
          {following.map((followedUser: any, index: number) => (
            <React.Fragment key={followedUser.id}>
              <ListItem>
                <ListItemAvatar>
                  <Avatar src={followedUser.avatarUrl} alt={followedUser.name}>
                    {followedUser.name?.charAt(0)?.toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography variant="subtitle1" fontWeight="bold">
                      {followedUser.name}
                    </Typography>
                  }
                  secondary={
                    <div>
                      <Typography variant="body2" color="textSecondary">
                        {followedUser.email}
                      </Typography>
                      {followedUser.profession && (
                        <Typography variant="caption" color="textSecondary">
                          {followedUser.profession}
                          {followedUser.location && ` • ${followedUser.location}`}
                        </Typography>
                      )}
                    </div>
                  }
                />
              </ListItem>
              {index < following.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

// Компонент для отображения челленджей пользователя
const UserChallengesLoader = ({ userId }: { userId: string }) => {
  const [challenges, setChallenges] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadChallenges = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('http://localhost:4000/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({
          query: `
            query GetPublicChallenges {
              publicChallenges {
                id
                title
                description
                type
                status
                mode
                currentStreak
                bestStreak
                totalDays
                participantsCount
                isPublic
                startDate
                endDate
                createdAt
                updatedAt
                user {
                  id
                  name
                  email
                }
              }
            }
          `
        })
      });
      const result = await response.json();
      if (result.errors) throw new Error(result.errors[0].message);
      
      // Фильтруем челленджи по userId
      const userChallenges = (result.data.publicChallenges || []).filter(
        (challenge: any) => challenge.user.id === userId
      );
      setChallenges(userChallenges);
    } catch (err: any) {
      console.error('Error loading user challenges:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) loadChallenges();
  }, [userId, loadChallenges]);

  if (loading) return <div style={{ padding: '16px' }}>Загрузка челленджей...</div>;
  if (error) return <div style={{ padding: '16px', color: 'red' }}>Ошибка: {error}</div>;
  if (challenges.length === 0) return <div style={{ padding: '16px', color: '#666' }}>Нет созданных челленджей</div>;

  const challengeTypeChoices = [
    { id: 'QUIT_SMOKING', name: 'Бросить курить' },
    { id: 'QUIT_PORN', name: 'Бросить порно' },
    { id: 'QUIT_SUGAR', name: 'Бросить сахар' },
    { id: 'QUIT_ALCOHOL', name: 'Бросить алкоголь' },
    { id: 'EXERCISE', name: 'Упражнения' },
    { id: 'MEDITATION', name: 'Медитация' },
    { id: 'READING', name: 'Чтение' },
    { id: 'WATER_DRINKING', name: 'Пить воду' },
    { id: 'SLEEP_SCHEDULE', name: 'Режим сна' },
    { id: 'NO_SOCIAL_MEDIA', name: 'Без соцсетей' },
    { id: 'CUSTOM', name: 'Кастомный' },
  ];

  const challengeStatusChoices = [
    { id: 'ACTIVE', name: 'Активный' },
    { id: 'PAUSED', name: 'Приостановлен' },
    { id: 'COMPLETED', name: 'Завершен' },
    { id: 'FAILED', name: 'Провален' },
  ];

  return (
    <Card style={{ margin: '16px 0' }}>
      <CardHeader title={`🎯 Созданные челленджи (${challenges.length})`} />
      <CardContent>
        <List>
          {challenges.map((challenge: any, index: number) => (
            <React.Fragment key={challenge.id}>
              <ListItem style={{ flexDirection: 'column', alignItems: 'flex-start', padding: '16px' }}>
                <Typography variant="h6" style={{ marginBottom: '8px' }}>
                  {challenge.title}
                </Typography>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '8px' }}>
                  <Typography variant="body2">
                    <strong>Тип:</strong> {challengeTypeChoices.find(c => c.id === challenge.type)?.name || challenge.type}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Статус:</strong> {challengeStatusChoices.find(c => c.id === challenge.status)?.name || challenge.status}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Режим:</strong> {challenge.mode === 'GROUP' ? 'Групповой' : 'Личный'}
                  </Typography>
                </div>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '8px' }}>
                  <Typography variant="body2">
                    <strong>Участников:</strong> {challenge.participantsCount}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Текущая серия:</strong> {challenge.currentStreak} дней
                  </Typography>
                  <Typography variant="body2">
                    <strong>Лучшая серия:</strong> {challenge.bestStreak} дней
                  </Typography>
                </div>
                {challenge.description && (
                  <Typography variant="body2" style={{ color: '#666', marginBottom: '8px' }}>
                    {challenge.description}
                  </Typography>
                )}
                <Typography variant="caption" color="textSecondary">
                  Создан: {new Date(challenge.createdAt).toLocaleDateString('ru-RU')}
                  {challenge.startDate && ` • Начало: ${new Date(challenge.startDate).toLocaleDateString('ru-RU')}`}
                  {challenge.endDate && ` • Конец: ${new Date(challenge.endDate).toLocaleDateString('ru-RU')}`}
                </Typography>
              </ListItem>
              {index < challenges.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

// Компонент для отображения заказов пользователя
const UserOrdersLoader = ({ userId }: { userId: string }) => {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('http://localhost:4000/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({
          query: `
            query GetAllSupplementOrders($limit: Float!, $offset: Float!) {
              allSupplementOrders(limit: $limit, offset: $offset) {
                id
                quantity
                totalPrice
                status
                isPaid
                paidAt
                challengeContext
                recommendationReason
                createdAt
                updatedAt
                user {
                  id
                  name
                  email
                }
                supplement {
                  id
                  name
                  price
                  emoji
                }
              }
            }
          `,
          variables: { limit: 1000, offset: 0 }
        })
      });
      const result = await response.json();
      if (result.errors) throw new Error(result.errors[0].message);
      
      // Фильтруем заказы по userId
      const userOrders = (result.data.allSupplementOrders || []).filter(
        (order: any) => order.user.id === userId
      );
      setOrders(userOrders);
    } catch (err: any) {
      console.error('Error loading user orders:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) loadOrders();
  }, [userId, loadOrders]);

  if (loading) return <div style={{ padding: '16px' }}>Загрузка заказов...</div>;
  if (error) return <div style={{ padding: '16px', color: 'red' }}>Ошибка: {error}</div>;
  if (orders.length === 0) return <div style={{ padding: '16px', color: '#666' }}>Нет заказов</div>;

  const orderStatusChoices = [
    { id: 'PENDING', name: 'Ожидает' },
    { id: 'PROCESSING', name: 'Обрабатывается' },
    { id: 'SHIPPED', name: 'Отправлен' },
    { id: 'DELIVERED', name: 'Доставлен' },
    { id: 'CANCELLED', name: 'Отменен' },
  ];

  return (
    <Card style={{ margin: '16px 0' }}>
      <CardHeader title={`📦 Заказы добавок (${orders.length})`} />
      <CardContent>
        <List>
          {orders.map((order: any, index: number) => (
            <React.Fragment key={order.id}>
              <ListItem style={{ flexDirection: 'column', alignItems: 'flex-start', padding: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginBottom: '8px' }}>
                  <Typography variant="h6">
                    {order.supplement.emoji} {order.supplement.name}
                  </Typography>
                  <Typography variant="h6" color="primary">
                    ${order.totalPrice?.toFixed(2)}
                  </Typography>
                </div>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '8px' }}>
                  <Typography variant="body2">
                    <strong>ID заказа:</strong> {order.id}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Количество:</strong> {order.quantity}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Статус:</strong> {orderStatusChoices.find(s => s.id === order.status)?.name || order.status}
                  </Typography>
                  <Typography variant="body2" style={{ color: order.isPaid ? 'green' : 'orange' }}>
                    <strong>Оплата:</strong> {order.isPaid ? '✅ Оплачен' : '⏳ Не оплачен'}
                  </Typography>
                </div>
                {order.challengeContext && (
                  <Typography variant="body2" style={{ color: '#666', marginBottom: '4px' }}>
                    <strong>Контекст:</strong> {order.challengeContext}
                  </Typography>
                )}
                {order.recommendationReason && (
                  <Typography variant="body2" style={{ color: '#666', marginBottom: '4px' }}>
                    <strong>Причина рекомендации:</strong> {order.recommendationReason}
                  </Typography>
                )}
                <Typography variant="caption" color="textSecondary">
                  Создан: {new Date(order.createdAt).toLocaleDateString('ru-RU')}
                  {order.paidAt && ` • Оплачен: ${new Date(order.paidAt).toLocaleDateString('ru-RU')}`}
                </Typography>
              </ListItem>
              {index < orders.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

export const UserShow = () => (
  <Show>
    <TabbedShowLayout>
      <Tab label="Основная информация">
        <TextField source="id" label="ID" />
        <TextField source="name" label="Имя" />
        <EmailField source="email" label="Email" />
        <NumberField source="age" label="Возраст" />
        <TextField source="profession" label="Профессия" />
        <TextField source="company" label="Компания" />
        <TextField source="location" label="Местоположение" />
        <TextField source="phone" label="Телефон" />
        <TextField source="bio" label="Био" />
        <UrlField source="avatarUrl" label="Аватар" />
        <DateField source="createdAt" label="Дата регистрации" showTime />
        <DateField source="updatedAt" label="Последнее обновление" showTime />
      </Tab>

      <Tab label="Навыки">
        <FunctionField 
          label="Навыки" 
          render={(record: any) => 
            record.skills?.map((skill: string, index: number) => (
              <span key={index} style={{ 
                display: 'inline-block', 
                padding: '4px 8px', 
                margin: '2px', 
                backgroundColor: '#e3f2fd', 
                borderRadius: '4px',
                fontSize: '0.875rem'
              }}>
                {skill}
              </span>
            )) || 'Нет навыков'
          } 
        />
      </Tab>

      <Tab label="Кошельки">
        <TextField source="walletAddress" label="ETH кошелек" />
        <NumberField source="walletChainId" label="Chain ID" />
        <DateField source="walletConnectedAt" label="Дата подключения ETH" showTime />
        <TextField source="tonWalletAddress" label="TON кошелек" />
        <DateField source="tonWalletConnectedAt" label="Дата подключения TON" showTime />
      </Tab>

      <Tab label="Telegram">
        <BooleanField source="telegramLinked" label="Подключен" />
        <TextField source="telegramId" label="ID" />
        <TextField source="telegramUsername" label="Username" />
        <TextField source="telegramFirstName" label="Имя" />
        <TextField source="telegramLastName" label="Фамилия" />
        <UrlField source="telegramPhotoUrl" label="Фото" />
        <TextField source="telegramLanguageCode" label="Язык" />
        <BooleanField source="telegramIsPremium" label="Premium" />
        <DateField source="telegramLinkedAt" label="Дата подключения" showTime />
      </Tab>

      <Tab label="Доставка">
        <TextField source="shippingCountry" label="Страна" />
        <TextField source="shippingCity" label="Город" />
        <TextField source="shippingAddress" label="Адрес" />
        <TextField source="shippingZipCode" label="Индекс" />
        <TextField source="shippingNotes" label="Примечания" />
      </Tab>

      <Tab label="Безопасность">
        <BooleanField source="twoFactorEnabled" label="2FA включен" />
      </Tab>

      <Tab label="Социальные связи">
        <div style={{ marginBottom: '16px' }}>
          <NumberField source="followersCount" label="Количество подписчиков" />
          <NumberField source="followingCount" label="Количество подписок" />
        </div>
        
        <FunctionField 
          label="Детали социальных связей"
          render={(record: any) => (
            <div>
              <FollowersLoader userId={record.id} />
              <FollowingLoader userId={record.id} />
            </div>
          )}
        />
      </Tab>

      <Tab label="Челленджи">
        <FunctionField 
          label="Созданные челленджи"
          render={(record: any) => (
            <UserChallengesLoader userId={record.id} />
          )}
        />
      </Tab>

      <Tab label="Заказы">
        <FunctionField 
          label="Заказы добавок"
          render={(record: any) => (
            <UserOrdersLoader userId={record.id} />
          )}
        />
      </Tab>
    </TabbedShowLayout>
  </Show>
);
